import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Actions, createEffect, ofType, OnInitEffects } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { catchError, map, switchMap, timeout } from 'rxjs/operators';
import { forkJoin, Observable, of } from 'rxjs';
import { AppConfigService, AppConfig } from '@core/services/config/app-config.service';
import { ApplicationHttpService, ApplicationInfoDto } from '@core/services/api-client.generated';
import { environmentActions } from '@core/state/environment/environment.actions';
import {
  ApplicationInfo,
  DatabaseType,
  ServiceVersionInfo,
  ServiceVersionsState,
  ServiceVersionStatus,
} from '@core/state/environment/environment.state';

/** Response from /health/live endpoints */
interface HealthCheckResponse {
  status?: string;
  version?: string;
  gitVersion?: string;
  gitSha?: string;
  [key: string]: unknown;
}

@Injectable()
export class EnvironmentEffects implements OnInitEffects {
  private readonly actions$ = inject(Actions);
  private readonly applicationService = inject(ApplicationHttpService);
  private readonly appConfig = inject(AppConfigService);
  private readonly http = inject(HttpClient);

  /**
   * Dispatch loadEnvironmentInfo when effects are initialized.
   * This ensures the action is dispatched AFTER effects are registered,
   * avoiding the timing issue with APP_BOOTSTRAP_LISTENER.
   */
  ngrxOnInitEffects(): Action {
    return environmentActions.loadEnvironmentInfo();
  }

  /**
   * Effect to load environment info from the API.
   * On failure, creates a fallback based on frontend config.
   */
  loadEnvironmentInfo$ = createEffect(() =>
    this.actions$.pipe(
      ofType(environmentActions.loadEnvironmentInfo),
      switchMap(() =>
        this.applicationService.getApplicationInfo().pipe(
          map((dto) => environmentActions.loadEnvironmentInfoSuccess({ info: this.mapDtoToApplicationInfo(dto) })),
          catchError((err) => {
            console.error('[EnvironmentEffects] Failed to fetch application info:', err);
            // Return fallback info based on frontend config
            const fallbackInfo = this.createFallbackInfo();
            return of(environmentActions.loadEnvironmentInfoSuccess({ info: fallbackInfo }));
          })
        )
      )
    )
  );

  /**
   * Maps the API DTO to the frontend ApplicationInfo interface.
   * Adds default values for fields not yet present in the DTO (databaseLocation, databaseType).
   */
  private mapDtoToApplicationInfo(dto: ApplicationInfoDto): ApplicationInfo {
    return {
      ...dto,
      databaseLocation: (dto as unknown as Record<string, unknown>)['databaseLocation'] as string ?? 'Unknown',
      databaseType: ((dto as unknown as Record<string, unknown>)['databaseType'] as DatabaseType) ?? 'unknown',
    };
  }

  /**
   * Creates fallback application info from frontend config.
   * Used when the API is unavailable.
   */
  private createFallbackInfo(): ApplicationInfo {
    const config = this.appConfig.config;
    return {
      environment: config.runtimeEnvironment,
      databaseName: 'unknown',
      databaseLocation: 'Unknown',
      databaseType: 'unknown',
      prNumber: this.extractPrNumberFromHostname(),
      isProduction: config.runtimeEnvironment.toLowerCase() === 'production',
      apiVersion: config.gitVersion,
      apiFullVersion: config.gitInformationalVersion,
      monorepoSha: config.gitSha?.trim() || 'unknown',
      s3NotifierVersion: null, // S3 Notifier version comes from API only
    };
  }

  /**
   * Extract PR number from hostname pattern: pr-{number}.syrf.org.uk
   * Returns null if hostname doesn't match preview pattern.
   */
  private extractPrNumberFromHostname(): string | null {
    const match = window.location.hostname.match(/^pr-(\d+)\./);
    return match ? match[1] : null;
  }

  // ============================================================================
  // Service Versions Effect
  // ============================================================================

  /**
   * Effect to load service versions from health endpoints.
   * Triggered when dialog opens or refresh is requested.
   */
  loadServiceVersions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(environmentActions.loadServiceVersions),
      switchMap(() => {
      const config = this.appConfig.config;
        return this.fetchAllServiceVersions(config).pipe(
          map((serviceVersions) =>
            environmentActions.loadServiceVersionsSuccess({ serviceVersions })
          ),
          catchError((err) => {
            console.error('[EnvironmentEffects] Failed to fetch service versions:', err);
            return of(environmentActions.loadServiceVersionsError({ error: err.message }));
          })
        );
      })
    )
  );

  /**
   * Fetch health info from all services.
   * Returns Observable<ServiceVersionsState> with best-effort results.
   */
  private fetchAllServiceVersions(config: AppConfig): Observable<ServiceVersionsState> {
    const isPreview = config.runtimeEnvironment.toLowerCase() === 'preview';
    const apiOrigin = config.apiOrigin;

    // Derive PM and Quartz origins for preview environments
    const pmOrigin = config.pmOrigin ||
      (isPreview && apiOrigin ? apiOrigin.replace('://api.', '://project-management.') : null);
    const quartzOrigin = config.quartzOrigin ||
      (isPreview && apiOrigin ? apiOrigin.replace('://api.', '://quartz.') : null);

    // Create observables for each service
    const api$ = this.fetchHealthEndpoint(apiOrigin, 'API Service');
    const pm$ = this.fetchHealthEndpoint(pmOrigin, 'Project Management Service');
    const quartz$ = this.fetchHealthEndpoint(quartzOrigin, 'Quartz Scheduler');

    // Note: S3 Notifier version is NOT included here.
    // It comes from ApplicationInfo and is derived via selector (single source of truth).
    return forkJoin([api$, pm$, quartz$]).pipe(
      map(([apiVersion, pmVersion, quartzVersion]) => ({
        loading: false,
        web: this.createWebVersionInfo(config),
        api: apiVersion,
        pm: pmVersion,
        quartz: quartzVersion,
      }))
    );
  }

  /**
   * Fetch health info from a single service endpoint.
   */
  private fetchHealthEndpoint(
    origin: string | null | undefined,
    serviceName: string
  ): Observable<ServiceVersionInfo> {
    if (!origin) {
      return of({
        name: serviceName,
        version: null,
        status: 'not-configured' as ServiceVersionStatus,
      });
    }

    return this.http.get<HealthCheckResponse>(`${origin}/health/live`).pipe(
      timeout(5000),
      map((response) => this.parseHealthResponse(serviceName, response)),
      catchError((error: HttpErrorResponse) => {
        // Try to extract health info from error response body
        const healthFromError = this.extractHealthFromError(error);
        if (healthFromError) {
          return of(this.parseHealthResponse(serviceName, healthFromError));
        }
        return of({
          name: serviceName,
          version: null,
          status: 'unavailable' as ServiceVersionStatus,
        });
      })
    );
  }

  /**
   * Create web version info from frontend config.
   */
  private createWebVersionInfo(config: AppConfig): ServiceVersionInfo {
    return {
      name: 'Web Application',
      version: config.displayVersion,
      status: 'healthy',
      sha: config.gitSha,
    };
  }

  /**
   * Parse health check response into ServiceVersionInfo.
   */
  private parseHealthResponse(
    name: string,
    response: HealthCheckResponse
  ): ServiceVersionInfo {
    const statusLower = response.status?.toLowerCase();
    const isHealthy = statusLower === 'healthy';
    const isDegraded = statusLower === 'degraded';
    return {
      name,
      version: response.version || response.gitVersion || 'Unknown',
      status: isHealthy ? 'healthy' : isDegraded ? 'degraded' : 'unhealthy',
      sha: response.gitSha as string | undefined,
      healthStatus: response.status,
    };
  }

  /**
   * Extract health response from HTTP error (e.g., 503 with valid JSON body).
   */
  private extractHealthFromError(error: HttpErrorResponse): HealthCheckResponse | null {
    if (error.error && typeof error.error === 'object' && 'status' in error.error) {
      return error.error as HealthCheckResponse;
    }
    return null;
  }
}
